import { useEffect, useState } from "react";
import { apiGet, apiPatch, apiPost } from "../api/http";
import { parseApiError } from "../utils/apiErrors";
import { useToast } from "../context/ToastContext";
import { isValidEmail, normalizeEmail } from "../utils/email";
import { formatDateTime } from "../utils/dateTime";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "unsubscribed", label: "Unsubscribed" },
];

function formatPrice(amount, currency = "USD") {
  const value = Number(amount || 0);
  const resolvedCurrency = String(currency || "USD").toUpperCase();
  return new Intl.NumberFormat(resolvedCurrency === "INR" ? "en-IN" : "en-US", {
    style: "currency",
    currency: resolvedCurrency,
    maximumFractionDigits: 0,
  }).format(value);
}

export default function AdminSubscribers({ inline }) {
  const { showError, showSuccess } = useToast();
  const [activeTab, setActiveTab] = useState("newsletter");

  // Newsletter State
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [email, setEmail] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
  const [rowStatusDrafts, setRowStatusDrafts] = useState({});
  const [savingId, setSavingId] = useState("");

  // Upgrade Requests State
  const [upgrades, setUpgrades] = useState([]);
  const [loadingUpgrades, setLoadingUpgrades] = useState(false);
  const [processingId, setProcessingId] = useState("");

  const loadSubscribers = async (nextPage = page, nextStatus = status, nextEmail = email) => {
    setLoading(true);
    try {
      const { data } = await apiGet("/api/newsletter/subscribers", {
        page: nextPage,
        limit,
        status: nextStatus || undefined,
        email: nextEmail || undefined,
      });
      setRows(Array.isArray(data?.data) ? data.data : []);
      const drafts = {};
      for (const row of data?.data || []) {
        drafts[row.id] = row.status;
      }
      setRowStatusDrafts(drafts);
      setPagination(
        data?.pagination || { page: nextPage, totalPages: 1, total: 0, limit }
      );
      setPage(nextPage);
    } catch (err) {
      showError(parseApiError(err, "Failed to load subscribers"));
    } finally {
      setLoading(false);
    }
  };

  const loadUpgrades = async () => {
    setLoadingUpgrades(true);
    try {
      const { data } = await apiGet("/api/admin/upgrade-requests");
      setUpgrades(Array.isArray(data) ? data : (data?.data || []));
    } catch (err) {
      showError(parseApiError(err, "Failed to load upgrade requests"));
    } finally {
      setLoadingUpgrades(false);
    }
  };

  useEffect(() => {
    loadSubscribers(1, "", "");
  }, []);

  const applyFilters = () => {
    const trimmedEmail = normalizeEmail(email);
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      showError("Please enter a valid email for filtering.");
      return;
    }
    setEmail(trimmedEmail);
    loadSubscribers(1, status, trimmedEmail);
  };

  const updateStatus = async (row) => {
    const nextStatus = rowStatusDrafts[row.id];
    if (!nextStatus || nextStatus === row.status) return;
    setSavingId(row.id);
    try {
      await apiPatch(`/api/newsletter/subscribers/${row.id}/status`, {
        status: nextStatus,
      });
      setRows((current) =>
        current.map((item) =>
          item.id === row.id
            ? { ...item, status: nextStatus, modified_at: new Date().toISOString() }
            : item
        )
      );
      showSuccess(`Status updated to ${nextStatus}.`);
    } catch (err) {
      showError(parseApiError(err, "Failed to update subscriber status"));
    } finally {
      setSavingId("");
    }
  };

  const handleApproveUpgrade = async (id) => {
    if (!window.confirm("Are you sure you want to APPROVE this upgrade request?")) return;
    setProcessingId(id);
    try {
      await apiPost(`/api/admin/upgrade-requests/${id}/approve`);
      showSuccess("Upgrade request approved successfully.");
      await loadUpgrades();
    } catch (err) {
      showError(parseApiError(err, "Failed to approve upgrade request"));
    } finally {
      setProcessingId("");
    }
  };

  const handleRejectUpgrade = async (id) => {
    if (!window.confirm("Are you sure you want to REJECT this upgrade request?")) return;
    setProcessingId(id);
    try {
      await apiPost(`/api/admin/upgrade-requests/${id}/reject`);
      showSuccess("Upgrade request rejected.");
      await loadUpgrades();
    } catch (err) {
      showError(parseApiError(err, "Failed to reject upgrade request"));
    } finally {
      setProcessingId("");
    }
  };

  return (
    <div className={inline ? "" : "page page-admin-wide"}>
      {!inline && (
        <>
          <h1 className="page-title">Platform Operations</h1>
          <p className="dashboard-lead" style={{ marginTop: "-8px" }}>
            Admin view for newsletter subscribers and workspace upgrade requests.
          </p>

          <div style={{ display: "flex", gap: 12, marginBottom: 20, borderBottom: "1px solid var(--border)", paddingBottom: 10 }}>
            <button
              type="button"
              className={`btn ${activeTab === "newsletter" ? "btn-primary" : "btn-ghost"}`}
              onClick={() => setActiveTab("newsletter")}
            >
              Newsletter Subscribers
            </button>
            <button
              type="button"
              className={`btn ${activeTab === "upgrades" ? "btn-primary" : "btn-ghost"}`}
              onClick={() => {
                setActiveTab("upgrades");
                loadUpgrades();
              }}
            >
              Plan Upgrade Requests
            </button>
          </div>
        </>
      )}

      {activeTab === "newsletter" ? (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="input-group" style={{ marginBottom: 0 }}>
              <select className="input" value={status} onChange={(e) => setStatus(e.target.value)} style={{ maxWidth: 200 }}>
                {STATUS_OPTIONS.map((item) => (
                  <option key={item.value || "all"} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              <input
                className="input"
                type="text"
                placeholder="Filter by email..."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              />
              <button type="button" className="btn btn-primary" onClick={applyFilters} disabled={loading}>
                {loading ? "Loading..." : "Apply"}
              </button>
            </div>
          </div>

          <div className="card">
            {loading ? <div className="loading">Loading subscribers...</div> : null}
            {!loading && rows.length === 0 ? (
              <div className="empty-state">No subscribers found for this filter.</div>
            ) : null}

            {!loading && rows.length > 0 ? (
              <>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Source</th>
                        <th>Created</th>
                        <th>Modified</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id}>
                          <td>{row.email}</td>
                          <td>
                            <select
                              className="input"
                              value={rowStatusDrafts[row.id] || row.status}
                              onChange={(e) =>
                                setRowStatusDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))
                              }
                              style={{ minWidth: 160, textTransform: "capitalize" }}
                              disabled={savingId === row.id}
                            >
                              <option value="pending">Pending</option>
                              <option value="active">Active</option>
                              <option value="unsubscribed">Unsubscribed</option>
                            </select>
                          </td>
                          <td>{row.source || "—"}</td>
                          <td>{formatDateTime(row.created_at)}</td>
                          <td>{formatDateTime(row.modified_at)}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              disabled={savingId === row.id || (rowStatusDrafts[row.id] || row.status) === row.status}
                              onClick={() => updateStatus(row)}
                            >
                              {savingId === row.id ? "Saving..." : "Save"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pagination" style={{ marginTop: 16 }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={page <= 1 || loading}
                    onClick={() => loadSubscribers(page - 1, status, email.trim())}
                  >
                    {"<- Prev"}
                  </button>
                  <span className="pagination-info">
                    Page {pagination.page} of {pagination.totalPages} — {pagination.total} total
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={page >= pagination.totalPages || loading}
                    onClick={() => loadSubscribers(page + 1, status, email.trim())}
                  >
                    {"Next ->"}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </>
      ) : (
        <div className="card">
          <h3 className="page-subtitle" style={{ marginTop: 0 }}>Pending Plan Upgrade Requests</h3>
          
          {loadingUpgrades ? (
            <div className="loading">Loading upgrade requests...</div>
          ) : upgrades.length === 0 ? (
            <div className="empty-state">No upgrade requests found.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Workspace</th>
                    <th>Requested By</th>
                    <th>Requested Plan</th>
                    <th>Price</th>
                    <th>Payment Ref</th>
                    <th>Status</th>
                    <th>Requested At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {upgrades.map((req) => {
                    const isPending = req.status === "pending";
                    let badgeClass = "info";
                    if (req.status === "approved") badgeClass = "ok";
                    if (req.status === "rejected") badgeClass = "error";

                    return (
                      <tr key={req.id}>
                        <td>
                          <strong>{req.workspace_name}</strong>
                          <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>ID: {req.workspace_id}</div>
                        </td>
                        <td>
                          {req.user_name}
                          <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{req.user_email}</div>
                        </td>
                        <td style={{ textTransform: "uppercase", fontWeight: 600 }}>{req.requested_plan}</td>
                        <td>{formatPrice(req.price, req.currency)}</td>
                        <td><code>{req.payment_reference || "—"}</code></td>
                        <td>
                          <span className={`pill-badge ${badgeClass}`} style={{ textTransform: "capitalize" }}>
                            {req.status}
                          </span>
                        </td>
                        <td>{formatDateTime(req.created_at)}</td>
                        <td>
                          {isPending ? (
                            <div style={{ display: "flex", gap: 6 }}>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={processingId === req.id}
                                onClick={() => handleApproveUpgrade(req.id)}
                                style={{ padding: "4px 8px", minHeight: "unset" }}
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                disabled={processingId === req.id}
                                onClick={() => handleRejectUpgrade(req.id)}
                                style={{ padding: "4px 8px", minHeight: "unset", color: "var(--danger)", borderColor: "var(--danger)" }}
                              >
                                Reject
                              </button>
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
